import { jsonLd } from '@trmediaab/zebra-utils';
import PropTypes from 'prop-types';
import { Fragment } from 'react';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';

import CommentsWrapper from '~/components/Comments';
import { Divider, SupTitle } from '~/components/Elements';
import ErrorBoundary from '~/components/ErrorBoundary';
import LiveBlog from '~/components/LiveBlog';
import MediaPlayer from '~/components/MediaPlayer';
import Quote from '~/components/Quote';
import TwoColumnLayout from '~/components/TwoColumnLayout';
import WysiwygText from '~/components/WysiwygText';
import { authorPropType, imagePropType } from '~/utils/prop-types';

import AccordionList from './AccordionList';
import FactBox from './FactBox';
import Footer from './Footer';
import Header from './Header';
import Image from './Image';

const chronicleDisclaimer =
  'Detta är en kommenterande text. Skribenten svarar för analys och ställningstagande i texten.';
const newsDisclaimer =
  ' Travronden jobbar med journalistik. Uppgifter som publiceras ska vara sanna och relevanta. Vi strävar efter förstahandskällor på vår information och att vara på plats där det händer. Vi följer de Pressetiska reglerna och vår nyhetsjournalistik ska präglas av trovärdighet och opartiskhet.';

const StyledArticle = styled.article`
  margin-bottom: ${props => (props.isRound ? undefined : '2.5rem')};
`;

const StyledArticleContent = styled.div`
  > * + * {
    margin-top: 1.5rem;
  }

  .editor__highlight {
    background-color: #fceb85;
  }

  .editor__fineprint {
    display: block;
    color: #9b9b9b;
    font-size: 14px;
  }
`;

const Article = ({ type, premiumLocked, hasPremium, article }) => {
  const {
    image,
    headline,
    preamble,
    comments,
    text,
    authors,
    publish_start: pubDate,
    video,
    paragraphs,
  } = article;

  const [author] = authors || [];
  const isChronicle = type === 'CHRONICLE' || type === 'ROUND_CHRONICLE';
  const isNews = type === 'NEWS' || type === 'ROUND_NEWS';
  const isPage = type === 'PAGE' || type === 'ABOUT_PAGE';
  const isBetType = type === 'BET_TYPE';
  const isRound = type === 'ROUND_TIP';
  const hasRoundHeader = [
    'ROUND_CHRONICLE',
    'ROUND_NEWS',
    'ROUND_TIP',
    'ROUND_TV',
  ].includes(type);

  const isTV = type === 'TV';
  const isSharedBet = type === 'SHARED_BET';

  const disclaimer = isChronicle
    ? chronicleDisclaimer
    : isNews
      ? newsDisclaimer
      : undefined;

  const factBoxes = paragraphs?.filter(p => p.type === 'fact_box');

  // Show aside if type of the article is BET_TYPE or if we have any fact_box paragraphs
  // Always hide aside for SHARED_BET.
  const aside = isSharedBet ? null : isBetType ? (
    <FactBox
      image={image}
      text={paragraphs?.find(p => p.type === 'fact_box')?.text}
    />
  ) : factBoxes && factBoxes.length > 0 ? (
    <>
      {factBoxes.map((p, idx) => (
        <FactBox key={`fb${idx}`} text={p.text} />
      ))}
    </>
  ) : null;

  const section = window.location.href.split('/')[3];
  const imageId = image
    ? image.image_id
    : type === 'CHRONICLE'
      ? authors[0]?.image2?.image_id
      : '';
  const url = window.location.href.split('?')[0];
  const site = 'travronden_spel';

  return (
    <StyledArticle isRound={isRound}>
      <ErrorBoundary showFallback={false}>
        <Helmet>
          <script type="application/ld+json">
            {jsonLd.article({ article, section, imageId, url, site })}
          </script>
        </Helmet>
      </ErrorBoundary>

      {!hasRoundHeader && (isChronicle || isNews || isTV || isSharedBet) && (
        <SupTitle>
          {isChronicle && 'Krönika'}
          {isNews && 'Nyhet'}
          {isTV && 'TV'}
          {isSharedBet && 'Andelsspel'}
        </SupTitle>
      )}

      <Header
        headline={headline}
        pubDate={pubDate}
        preamble={preamble}
        author={author}
        quoteHeadline={isChronicle}
        showAuthor={isChronicle}
      />
      {isChronicle && <Divider marginTop="0" />}
      {video && <MediaPlayer video={video} />}
      {!premiumLocked && (
        <Fragment>
          {(isNews || isPage || isRound) && video == null && (
            <Image image={image} />
          )}
          {!isSharedBet && (
            <TwoColumnLayout aside={aside}>
              <StyledArticleContent>
                {/* If the content is not yet upgraded to paragraphs, show content.text. */}
                {!paragraphs?.some(item => item.type === 'html') && (
                  <WysiwygText html={text} />
                )}

                {paragraphs?.map(
                  (content, idx) =>
                    (content.type === 'html' && (
                      <WysiwygText key={idx} html={content.content} />
                    )) ||
                    (content.type === 'expandable_content' && (
                      <AccordionList key={content.headline} content={content} />
                    )) ||
                    (content.type === 'archive_images' && (
                      <Fragment key={idx}>
                        {content.images.map(image => (
                          <Image
                            key={image.cdn_id}
                            image={{ ...image, image_id: image.cdn_id }}
                          />
                        ))}
                      </Fragment>
                    )) ||
                    (content.type === 'video' && (
                      <MediaPlayer key={content.url} video={content} />
                    )) ||
                    (content.type === 'quote' && (
                      <Quote
                        key={idx}
                        quote={content.quote}
                        byline={content.byline}
                      />
                    )) ||
                    (content.type === 'liveblog' && (
                      <LiveBlog
                        key={idx}
                        hasPremium={hasPremium}
                        paragraph={content}
                      />
                    )),
                )}
              </StyledArticleContent>
            </TwoColumnLayout>
          )}

          <Footer
            author={author}
            pubDate={pubDate}
            disclaimer={disclaimer}
            showAuthor={isNews}
            comments={
              comments !== CommentsWrapper.NONE ? (
                <CommentsWrapper article={article} hasPremium={hasPremium} />
              ) : null
            }
          />
        </Fragment>
      )}
    </StyledArticle>
  );
};

Article.propTypes = {
  type: PropTypes.oneOf([
    'NEWS',
    'CHRONICLE',
    'PAGE',
    'ABOUT_PAGE',
    'ROUND_TIP',
    'TV',
    'BET_TYPE',
    'SHARED_BET',
  ]).isRequired,
  premiumLocked: PropTypes.bool.isRequired,
  hasPremium: PropTypes.bool,
  article: PropTypes.shape({
    image: imagePropType,
    headline: PropTypes.string.isRequired,
    preamble: PropTypes.string,
    text: PropTypes.string,
    authors: PropTypes.arrayOf(authorPropType),
    publish_start: PropTypes.string.isRequired,
    video: PropTypes.shape({
      url: PropTypes.string.isRequired,
    }),
    paragraphs: PropTypes.array,
  }).isRequired,
};

export default Article;
